import { ReactElement } from "react";

import { arrowBack, arrowForward } from "ionicons/icons";

import Icon from "../atoms/Icon";
import styled, { css, keyframes } from "styled-components";
import Button from "../atoms/Button";
import { useTranslation } from "react-i18next";

interface NextPrevButtonsProps {
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  nextText?: string;
  prevText?: string;
  /** Icon after the next label (default: forward arrow). */
  nextIcon?: string;
  /** Raising it plays a one-off pulse on the next button, to draw the eye to it. */
  nextPulseKey?: number;
  disablePrevious?: boolean;
  disableNext?: boolean;
  /**
   * "default": the edge-flush half pills every other screen uses (signup, login,
   * onboarding, content steps, the island map back button…) — unchanged.
   * "panel": the questionnaire screen's frameless button row (Précédent as a text
   * button, Suivant / Terminer as a full pill). Opt-in, so no other screen changes.
   * Both stay in the page flow (margin-top: auto): on a long question the
   * buttons sit below the last answer.
   */
  variant?: "default" | "panel";
}

const NextPrevButtons = ({
  onPreviousClick,
  onNextClick,
  nextText,
  prevText,
  nextIcon,
  nextPulseKey,
  disablePrevious,
  disableNext,
  variant = "default",
}: NextPrevButtonsProps): ReactElement => {
  const { t } = useTranslation();

  if (variant === "panel") {
    return (
      <Panel>
        {onPreviousClick && (
          <PanelPrevButton
            type="button"
            disabled={disablePrevious}
            onClick={onPreviousClick}
          >
            <Icon icon={arrowBack} />
            {prevText || t("common_previous")}
          </PanelPrevButton>
        )}

        {onNextClick && (
          <PanelNextButton
            // A new key remounts the button, which replays its pulse.
            key={nextPulseKey}
            type="button"
            $pulse={Boolean(nextPulseKey)}
            disabled={disableNext}
            onClick={onNextClick}
          >
            {nextText || t("common_next")}
            <Icon icon={nextIcon ?? arrowForward} />
          </PanelNextButton>
        )}
      </Panel>
    );
  }

  return (
    <Buttons>
      {onPreviousClick && (
        <PrevButton disabled={disablePrevious} onClick={onPreviousClick}>
          <Icon icon={arrowBack} />
          {prevText || t("common_previous")}
        </PrevButton>
      )}

      {onNextClick && (
        <NextButton
          // A new key remounts the button, which replays its pulse.
          key={nextPulseKey}
          $pulse={Boolean(nextPulseKey)}
          disabled={disableNext}
          onClick={onNextClick}
        >
          {nextText || t("common_next")}
          <Icon icon={nextIcon ?? arrowForward} />
        </NextButton>
      )}
    </Buttons>
  );
};

const Buttons = styled.div`
 
  bottom: 0px;
  z-index: 1;
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin-top: auto;
  padding-bottom: 10px;
  position: relative;
  flex-direction: row;
  
  // @media (max-height: 400px) {
  //   position: fixed;
  // }
`;

const PrevButton = styled(Button)`
  border-radius: 0 30px 30px 0;
  padding-left: 10px;
  margin-right: auto;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  @media (min-width: 800px) {
    border-radius: 30px 30px 30px 30px;
    padding-right: 20px;
    margin-left: 20px;
  }
`;

/* One soft pulse: a slight swell and a ring in the button's own color fading out. */
const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1),
      0 0 0 0 rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0.6);
  }
  40% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
    box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.1),
      0 0 0 14px rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0);
  }
`;

const NextButton = styled(Button)<{ $pulse?: boolean }>`
  border-radius: 30px 0 0 30px;
  padding-right: 10px;
  margin-left: auto;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};
  /* Flush against the right edge on phones: swell from that edge, not past it. */
  transform-origin: right center;
  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${pulse} 0.8s ease-out;
    `}
  @media (min-width: 800px) {
    border-radius: 30px 30px 30px 30px;
    padding-left: 20px;
    margin-right: 20px;
    transform-origin: center;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* --- "panel" variant (questionnaire screen) --- */

/* The button row sits straight on the page, no frame of its own (a surface
   panel crowded the screen); in the flow like the default variant. */
const Panel = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 20px calc(18px + var(--ion-safe-area-bottom, 0px));

  @media (min-width: 800px) {
    margin-bottom: 20px;
  }
`;

const PanelPrevButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 52px;
  padding: 0 14px 0 6px;
  border: none;
  border-radius: var(--app-radius-pill, 999px);
  background: none;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.75);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--app-color-ink-rgb, 37, 99, 235), 0.35);
  }
`;

const PanelNextButton = styled.button<{ $pulse?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-left: auto;
  min-height: 56px;
  min-width: 168px;
  padding: 0 26px;
  border: none;
  border-radius: var(--app-radius-pill, 999px);
  background: var(--app-color-secondary, #fecf48);
  color: var(--app-color-secondary-contrast, #041723);
  box-shadow: 0 12px 22px -14px rgba(var(--app-color-secondary-rgb, 254, 207, 72), 0.95);
  font-family: var(--app-font-heading, inherit);
  font-size: 17.5px;
  font-weight: var(--app-font-heading-weight, 700);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  ${({ $pulse }) =>
    $pulse &&
    css`
      animation: ${pulse} 0.8s ease-out;
    `}

  &:active {
    background: var(--app-color-secondary-shade, #e0b63f);
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--app-color-ink-rgb, 37, 99, 235), 0.35);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export default NextPrevButtons;
